import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import BackButton from "../components/BackButton";
import LoadingOverlay from "../components/LoadingOverlay";
import PaginationControls from "../components/PaginationControls";
import {
  getAnnouncementDetail,
  getAnnouncementLogs,
} from "../services/announcementService";
import { formatAnnouncementDateTime } from "../utils/announcementUtils";

const AnnouncementLogs = () => {
  const { id } = useParams();
  const [logs, setLogs] = useState([]);
  const [announcementTitle, setAnnouncementTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchLogs = useCallback(async () => {
    if (!id) return;

    setLoading(true);

    try {
      const [detailResponse, logsResponse] = await Promise.all([
        getAnnouncementDetail(id),
        getAnnouncementLogs(id, { page: currentPage, limit: 20 }),
      ]);

      setAnnouncementTitle(detailResponse?.data?.title || detailResponse?.title || "");
      setLogs(Array.isArray(logsResponse?.data) ? logsResponse.data : []);
      setTotalPages(Number(logsResponse?.totalPages || 1));
      setTotal(Number(logsResponse?.total || 0));
    } catch (error) {
      console.error("Fetch announcement logs error:", error);
      toast.error(error?.response?.data?.message || "โหลดประวัติประกาศไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  }, [currentPage, id]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <div className="relative min-h-screen bg-gray-50 px-4 py-6 transition-colors duration-300 dark:bg-gray-900 sm:px-6">
      <LoadingOverlay loading={loading} />

      <div className="mb-8">
        <div className="mb-3 flex items-center justify-between">
          <BackButton label="ย้อนกลับ" />
        </div>

        <div>
          <h1 className="text-[26px] font-semibold text-gray-900 dark:text-gray-100">
            Announcement Logs
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {announcementTitle
              ? `ประวัติการมองเห็นและการตอบรับสำหรับ "${announcementTitle}"`
              : "ประวัติการมองเห็นและการตอบรับของประกาศ"}
          </p>
        </div>
      </div>

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-100 px-5 py-4 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          ทั้งหมด {total} รายการ
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-600 dark:bg-gray-900/60 dark:text-gray-300">
              <tr>
                <th className="px-4 py-3 font-semibold">Username</th>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Department</th>
                <th className="px-4 py-3 font-semibold">Action</th>
                <th className="px-4 py-3 font-semibold">Version</th>
                <th className="px-4 py-3 font-semibold">Page Path</th>
                <th className="px-4 py-3 font-semibold">Acted At</th>
              </tr>
            </thead>
            <tbody>
              {logs.length > 0 ? (
                logs.map((log) => (
                  <tr
                    key={log._id}
                    className="border-t border-gray-100 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700/40"
                  >
                    <td className="px-4 py-4">{log.username || "-"}</td>
                    <td className="px-4 py-4">
                      {[log.firstName, log.lastName].filter(Boolean).join(" ") ||
                        log.nickname ||
                        "-"}
                    </td>
                    <td className="px-4 py-4">{log.role || "-"}</td>
                    <td className="px-4 py-4">{log.department || "-"}</td>
                    <td className="px-4 py-4 capitalize">{log.action || "-"}</td>
                    <td className="px-4 py-4">
                      <span className="rounded-full bg-cyan-100 px-3 py-1 text-xs font-medium text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-200">
                        v{log.announcementVersion ?? 1}
                      </span>
                    </td>
                    <td className="px-4 py-4">{log.pagePath || "-"}</td>
                    <td className="px-4 py-4">
                      {formatAnnouncementDateTime(log.actedAt)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    ยังไม่มีประวัติการตอบรับสำหรับประกาศนี้
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};

export default AnnouncementLogs;
